import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, IconButton } from '@mui/material';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import WorkflowTemplateEditor from './WorkflowTemplateEditor';
import { enhancedApi } from '../api';
import DeleteIcon from '@mui/icons-material/Delete';

const WorkflowTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await enhancedApi.getWorkflowTemplates();
      setTemplates(response.data?.results ?? response.data ?? []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load workflow templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (templateData) => {
    setSaving(true);
    setError(null);
    try {
      if (selectedTemplate?.id) {
        await enhancedApi.updateWorkflowTemplate(selectedTemplate.id, {
          name: templateData.name,
          description: templateData.description,
          category: templateData.category,
          estimated_duration_days: templateData.estimated_duration_days,
        });
      } else {
        const createRes = await enhancedApi.createWorkflowTemplate({
          name: templateData.name,
          description: templateData.description,
          category: templateData.category,
          estimated_duration_days: templateData.estimated_duration_days,
        });
        const newId = createRes.data?.id;
        if (newId && templateData.tasks?.length) {
          for (let i = 0; i < templateData.tasks.length; i++) {
            const task = templateData.tasks[i];
            await enhancedApi.createWorkflowTaskTemplate(newId, {
              name: task.name,
              description: task.description || '',
              order: i + 1,
              task_type: task.task_type || 'manual',
              priority: task.priority || 'medium',
              due_date_offset: task.due_date_offset ?? 0,
              due_date_unit: task.due_date_unit || 'days',
            });
          }
        }
      }
      setOpen(false);
      setSelectedTemplate(null);
      await fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete workflow template "${template.name}"?`)) return;
    setDeletingId(template.id);
    setError(null);
    try {
      await enhancedApi.deleteWorkflowTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      if (selectedTemplate?.id === template.id) {
        setOpen(false);
        setSelectedTemplate(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to delete template');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedTemplate(null);
    setError(null);
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageHeaderActionsBar
        title="Workflow Templates"
        primaryAction={{ label: 'Create New Template', onClick: () => { setSelectedTemplate(null); setOpen(true); } }}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Tasks</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>{template.name}</TableCell>
                <TableCell>{template.description || '-'}</TableCell>
                <TableCell>{template.task_count ?? (template.tasks?.length ?? 0)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => { setSelectedTemplate(template); setOpen(true); }} disabled={saving}>Edit</Button>
                  <IconButton size="small" color="error" onClick={() => handleDelete(template)} disabled={deletingId === template.id} aria-label="Delete template">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open || !!selectedTemplate} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <WorkflowTemplateEditor
            template={selectedTemplate}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WorkflowTemplatesPage; 